// The account-entitlement axis of the UI, in one place.
//
// `utils/capabilities` answers "what may this user do with THIS project" from
// the per-project accessLevel the gate resolved. This file answers the other
// half — "what plan does the account hold" — and it exists because that
// question used to be answered eight different ways: `entitlements.hasUnlimited`
// re-derived inline in each modal, `workspace.isAtlas` on the switcher, and the
// brand string "Atlas" typed out at every render site.
//
// The wire now carries a LIST OF PLANS keyed by grant KIND, not a boolean per
// kind. That mirrors the server's entitlement model, where the kind is the open
// extension axis: a new plan kind reaches these helpers — and therefore every
// caller — without a new field on the response or a new flag in a component.
// It is also the shape that cannot repeat the divergence a per-kind boolean
// mirror caused on the account table.
export const UNLIMITED = 'unlimited';
/**
 * The one place the AccountPlan wire message is read. Every RPC that reports
 * account coverage — entitlements, the workspace switcher — maps through here,
 * so the shape can never drift between them.
 */
export function accountPlanFromProto(p) {
    var _a;
    return {
        kind: p.getKind(),
        displayName: p.getDisplayName(),
        expiresAt: ((_a = p.hasExpiresAt) === null || _a === void 0 ? void 0 : _a.call(p)) ? p.getExpiresAt() : undefined,
    };
}
/** The active plan of a given kind, if the account holds one. */
export function planOfKind(holder, kind) {
    var _a;
    return (_a = holder === null || holder === void 0 ? void 0 : holder.activePlans) === null || _a === void 0 ? void 0 : _a.find((p) => p.kind === kind);
}
/** Does the account hold an active plan of this kind? */
export function hasPlanKind(holder, kind) {
    return planOfKind(holder, kind) !== undefined;
}
// The unlimited plan is the one the UI branches on today: it lifts the
// per-project unlock charge, the extra-workspace wall and the editor-seat wall.
// These two are the ONLY sanctioned spellings of that question in the client.
export const unlimitedPlan = (holder) => planOfKind(holder, UNLIMITED);
export const hasUnlimitedPlan = (holder) => hasPlanKind(holder, UNLIMITED);
// Fallback label for the unlimited plan when nothing has been fetched yet (a
// wall rendered before entitlements resolve). The server is authoritative; this
// only keeps copy from reading "undefined" during the first paint.
const UNLIMITED_FALLBACK_LABEL = 'Atlas';
/**
 * What to call the unlimited plan in copy. Prefers the label the server sent
 * with the account's own plan, then the catalog's, then the fallback — so a
 * plan rename lands everywhere from one server-side constant.
 */
export function unlimitedPlanLabel(holder, catalog) {
    var _a, _b;
    return (((_a = unlimitedPlan(holder)) === null || _a === void 0 ? void 0 : _a.displayName) ||
        ((_b = catalog === null || catalog === void 0 ? void 0 : catalog.plans.find((p) => p.sku === 'unlimited_yearly')) === null || _b === void 0 ? void 0 : _b.displayName) ||
        UNLIMITED_FALLBACK_LABEL);
}
export function billingPosture(holder, paidProjectCount) {
    if (hasUnlimitedPlan(holder))
        return 'plan';
    return paidProjectCount > 0 ? 'payg' : 'free';
}
export function billingPostureLabel(posture, holder) {
    switch (posture) {
        case 'plan':
            return unlimitedPlanLabel(holder);
        case 'payg':
            return 'Pay-as-you-go';
        default:
            return 'Free';
    }
}
export function singleMapPrices(catalog) {
    var _a, _b;
    const plan = catalog === null || catalog === void 0 ? void 0 : catalog.plans.find((p) => p.sku === 'single_map');
    return {
        demoPriceCents: (_a = plan === null || plan === void 0 ? void 0 : plan.demoPriceCents) !== null && _a !== void 0 ? _a : 0,
        fullPriceCents: (_b = plan === null || plan === void 0 ? void 0 : plan.fullPriceCents) !== null && _b !== void 0 ? _b : 0,
    };
}
